import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import SingleSelect from "@/components/controls/SingleSelect";
import DateTimeField from "@/components/controls/DateTimeField";
import ContractField from "./ContractField";
import { ServiceDuration } from "@/pages/settings/pages/products-v2/types";

interface ServiceDatesSectionProps {
  serviceStartDate: string;
  serviceEndDate: string;
  isCustomServiceDuration: boolean;
  customServiceDuration: number | null;
  serviceDurationOptions: { id: ServiceDuration; displayName: string }[];
  showOptOutDate: boolean;
  optOutDate: string;
  isServiceDatePickerOpen: boolean;
  isOptOutDatePickerOpen: boolean;
  isCustomDurationSelected: boolean;
  isCustomServiceEndDatePickerOpen: boolean;
  hideOptOutDate?: boolean;
  // The Update terms editor pins the start date wherever something seeds it; a
  // correction seeds nothing, so a new line's stays editable.
  isServiceStartDateDisabled?: boolean;
  // Update terms lets a fixed term's end date move, so the period can be shortened or prolonged.
  isServiceEndDateEditable?: boolean;
  onServiceStartDateChange: (date: string) => void;
  onServiceDatePickerToggle: (open: boolean) => void;
  onCustomServiceDurationChange: (duration: number) => void;
  onShowOptOutDate: () => void;
  onOptOutDateChange: (date: string) => void;
  onOptOutDatePickerToggle: (open: boolean) => void;
  onCustomServiceEndDateChange: (date: string) => void;
  onCustomServiceEndDatePickerToggle: (open: boolean) => void;
}

const ServiceDatesSection: React.FC<ServiceDatesSectionProps> = ({
  serviceStartDate,
  serviceEndDate,
  isCustomServiceDuration,
  customServiceDuration,
  serviceDurationOptions,
  showOptOutDate,
  optOutDate,
  isServiceDatePickerOpen,
  isOptOutDatePickerOpen,
  isCustomDurationSelected,
  isCustomServiceEndDatePickerOpen,
  hideOptOutDate = false,
  isServiceStartDateDisabled = false,
  isServiceEndDateEditable = false,
  onServiceStartDateChange,
  onServiceDatePickerToggle,
  onCustomServiceDurationChange,
  onShowOptOutDate,
  onOptOutDateChange,
  onOptOutDatePickerToggle,
  onCustomServiceEndDateChange,
  onCustomServiceEndDatePickerToggle
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex gap-2 items-start w-full">
        <ContractField label={t("contractTerms.serviceStartDate")} required>
          <DateTimeField
            type="date"
            value={serviceStartDate}
            onChange={onServiceStartDateChange}
            isOpen={isServiceDatePickerOpen}
            onOpenChange={onServiceDatePickerToggle}
            name="service-start-date"
            placeholder={t("contractTerms.startDate")}
            disabled={isServiceStartDateDisabled}
          />
        </ContractField>

        {isCustomServiceDuration && (
          <ContractField label={t("contractTerms.duration")} required>
            <SingleSelect
              fieldName="custom-service-duration"
              options={serviceDurationOptions}
              selectedValue={customServiceDuration?.toString() || ""}
              onSelection={(value) =>
                onCustomServiceDurationChange(Number(value))
              }
              placeholder={t("select")}
              variant="form"
              className="w-full"
            />
          </ContractField>
        )}

        <ContractField
          label={t("contractTerms.serviceEndDate")}
          required={isCustomDurationSelected}
        >
          {isCustomDurationSelected || isServiceEndDateEditable ? (
            <DateTimeField
              type="date"
              value={serviceEndDate}
              onChange={onCustomServiceEndDateChange}
              isOpen={isCustomServiceEndDatePickerOpen}
              onOpenChange={onCustomServiceEndDatePickerToggle}
              name="service-end-date"
              availableFrom={serviceStartDate || undefined}
              placeholder={t("contractTerms.endDate")}
            />
          ) : serviceStartDate &&
            (!isCustomServiceDuration || customServiceDuration) ? (
            <DateTimeField
              type="date"
              value={serviceEndDate}
              onChange={() => {}}
              isOpen={false}
              onOpenChange={() => {}}
              name="service-end-date"
              className="pointer-events-none opacity-70"
            />
          ) : (
            <Text variant="body" className="mt-1 ml-3">
              —
            </Text>
          )}
        </ContractField>
      </div>

      {!hideOptOutDate && (
        <div className="flex flex-col gap-2 items-start w-full">
          {!showOptOutDate ? (
            <Button
              variant="tertiary"
              onClick={onShowOptOutDate}
              disabled={!serviceStartDate || !serviceEndDate}
            >
              <span
                className={
                  !serviceStartDate || !serviceEndDate
                    ? "text-text-disabled"
                    : "text-text-cta"
                }
              >
                {t("contractTerms.addOptOutDate")}
              </span>
            </Button>
          ) : (
            <ContractField label={t("contractTerms.optOutDate")}>
              <DateTimeField
                type="date"
                value={optOutDate}
                onChange={onOptOutDateChange}
                isOpen={isOptOutDatePickerOpen}
                onOpenChange={onOptOutDatePickerToggle}
                name="opt-out-date"
                placeholder={t("contractTerms.selectOptOutDate")}
                availableFrom={serviceStartDate || undefined}
                availableTo={serviceEndDate || undefined}
              />
            </ContractField>
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceDatesSection;
